import clsx from 'clsx'
import type { PropsWithChildren } from 'react'
import React from 'react'

type GroupDirection = 'row' | 'col'

export interface CheckboxGroupProps {
  /**
   * If a visible label isn't specified for a CheckboxGroup, an aria-label must
   * be provided for accessibility
   */
  ariaLabel?: string
  /**
   * The layout direction of the checkbox group.
   * Use `row` for horizontal, `col` for vertical
   * @default 'col'
   */
  direction?: GroupDirection
  /**
   * Whether the checkbox group is in an error state
   * @default false
   * */
  error?: boolean
  /**
   * Additional descriptive text or error text to display below the checkbox group
   * @default ''
   */
  helperText?: string
}

function Group({
  children,
  ariaLabel,
  error = false,
  helperText = '',
  direction = 'col',
}: PropsWithChildren<CheckboxGroupProps>): JSX.Element {
  return (
    <div>
      <div
        {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
        className={clsx(
          `flex flex-${direction}`,
          direction === 'row' ? 'items-center' : '',
        )}
        role="group"
      >
        {children}
      </div>
      {helperText ? (
        <span
          aria-live={error ? 'assertive' : 'off'}
          className={clsx(
            'text-xs',
            error ? 'text-error-main' : 'text-black-60',
          )}
        >
          {helperText}
        </span>
      ) : null}
    </div>
  )
}

export default Group
